import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
  HttpStatusCode,
  HttpContextToken,
} from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { catchError, tap } from 'rxjs/operators';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';

export const IGNORE_SENTRY = new HttpContextToken<boolean>(() => false);

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  constructor(private router: Router, private translate: TranslateService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // TODO - remove this, when language as get parameter is no longer needed
    const isOldApi =
      req.url.match('health-intelligence') ||
      req.url.match('/api/dna/reports') ||
      req.url.match('/api/habits/analyses') ||
      req.url.match('/api/dna/analyses');

    let clone = req.clone();
    const currentLang = this.translate?.currentLang;

    if (currentLang) {
      const langLongCode =
        environment.locations.find(
          (location) => location.lang.code == currentLang
        )?.lang?.longCode || environment.locations[0].lang.longCode;

      const updateHeaders = {
        setHeaders: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'X-Language': langLongCode,
        },
      };

      if (isOldApi) {
        Object.assign(updateHeaders);
      }
      clone = req.clone(updateHeaders);
    }

    return next.handle(clone).pipe(
      tap(getCorrelation),
      catchError((error) => this.catchHttpError(error, req))
    );
  }

  private catchHttpError(error: HttpEvent<any>, req: HttpRequest<any>) {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        this.router.navigateByUrl(`/accounts/check`);
        return EMPTY;
      }

      saveCorrelationToSentry(error);

      // For http 0, 403 or 500 log to Sentry
      const unexpectedStatuses = [
        0, // No response
        HttpStatusCode.Forbidden,
        HttpStatusCode.InternalServerError,
      ];

      if (unexpectedStatuses.includes(error.status)) {
        const url = error?.url?.replace(environment.apiUrl, '');

        if(!req.context.get(IGNORE_SENTRY)) {
          Sentry.captureException(`[API error] [Status: ${error.status}] ${url}`);
        }
      }
    }
    return throwError(error);
  }
}

const saveCorrelationToSentry = (
  data: HttpErrorResponse | HttpResponse<any>
): void => {
  const payload = {
    correlationId: data.headers.get('CorrelationId'),
  };
  Sentry.setContext('Last API call', payload);
};

const getCorrelation = (event: HttpEvent<any>) => {
  if (event instanceof HttpResponse) {
    saveCorrelationToSentry(event);
  }
};
